import { Button, ButtonVariants } from '@triumph/design-system';
import { useId, useState } from 'react';

import { MentionTextarea, MentionSuggestion } from '@/components/shared/components/MentionTextarea';

import { ButtonsWrapper, ErrorText, FieldLabel, Wrapper } from './ActivityNoteForm.StyledComponents';

interface ActivityNoteFormProps {
    onSubmit: (args: { note: string }) => Promise<void>;
    disabled: boolean;
    loading: boolean;
    placeholder: string;
    suggestions: MentionSuggestion[];
}

/**
 * Replacement for the Stepper's built-in `AddNewNote`, which owns a plain
 * `<textarea>` we can't inject mention typeahead into. This renders the same
 * label + input + right-aligned Add button, but the input is a
 * {@link MentionTextarea}. It is rendered as a sibling above the `<Stepper>`
 * (see ApplicantActivity index.tsx), and the Stepper's auto-injected AddNewNote
 * is hidden via CSS to avoid duplication.
 */
export const ActivityNoteForm = ({
    onSubmit,
    disabled,
    loading,
    placeholder,
    suggestions,
}: ActivityNoteFormProps) => {
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);
    const inputId = useId();

    const handleAdd = async () => {
        const trimmed = note.trim();
        if (!trimmed || disabled || loading) return;

        try {
            await onSubmit({ note: trimmed });
            setNote('');
            setError(null);
        } catch (err) {
            console.error('Failed to save internal note:', err);
            setError('Failed to save note. Please try again.');
        }
    };

    return (
        <Wrapper data-testid="activity-note-form">
            <FieldLabel htmlFor={inputId}>New Internal Note</FieldLabel>
            <MentionTextarea
                id={inputId}
                value={note}
                onChange={(val) => {
                    setNote(val);
                    if (error) setError(null);
                }}
                placeholder={placeholder}
                disabled={disabled}
                spellCheck
                suggestions={suggestions}
                data-testid="activity-note-input"
                hasError={Boolean(error)}
            />
            {error ? <ErrorText>{error}</ErrorText> : null}
            <ButtonsWrapper>
                <Button
                    variant={ButtonVariants.primary}
                    onClick={handleAdd}
                    disabled={disabled}
                    loading={loading}
                    data-testid="activity-add-note"
                >
                    Add Internal Note
                </Button>
            </ButtonsWrapper>
        </Wrapper>
    );
};
